/**
 * Lista de CFOPs (Código Fiscal de Operações e Prestações)
 * Conforme Resolução 65/2008 do CONFAZ
 *
 * Estrutura do CFOP: X.XXX
 * - Primeiro dígito: 1-9 (tipo de operação)
 * - Próximos 3 dígitos: 001-999 (especificação)
 */

export interface CFOP {
  codigo: string;
  descricao: string;
  categoria: string;
  tipo: 'entrada' | 'saida';
}

export const CFOPs: CFOP[] = [
  // ============================================
  // OPERAÇÕES COM MERCADORIAS - SAÍDA (5000)
  // ============================================
  {
    codigo: '5102',
    descricao: 'Venda de mercadoria adquirida ou recebida de terceiros',
    categoria: 'SAÍDA - Venda para Consumidor Final',
    tipo: 'saida'
  },
  {
    codigo: '5103',
    descricao: 'Devolução de venda de mercadoria adquirida ou recebida de terceiros',
    categoria: 'SAÍDA - Devolução',
    tipo: 'saida'
  },
  {
    codigo: '5104',
    descricao: 'Venda de amostra de mercadoria adquirida ou recebida de terceiros',
    categoria: 'SAÍDA - Amostra Grátis',
    tipo: 'saida'
  },
  {
    codigo: '5105',
    descricao: 'Venda de mercadoria ou de returno de mercadoria de clientes',
    categoria: 'SAÍDA - Outros',
    tipo: 'saida'
  },
  {
    codigo: '5120',
    descricao: 'Venda de mercadoria adquirida ou recebida de terceiros (Revenda)',
    categoria: 'SAÍDA - Venda B2B',
    tipo: 'saida'
  },
  {
    codigo: '5125',
    descricao: 'Venda de mercadoria que o estabelecimento adquiriu para consignação',
    categoria: 'SAÍDA - Consignação',
    tipo: 'saida'
  },
  {
    codigo: '5130',
    descricao: 'Venda de mercadoria à Zona de Processamento de Exportação - ZPE',
    categoria: 'SAÍDA - Exportação',
    tipo: 'saida'
  },
  {
    codigo: '5210',
    descricao: 'Devolução de compra de mercadoria adquirida ou recebida de terceiros',
    categoria: 'SAÍDA - Devolução de Compra',
    tipo: 'saida'
  },

  // ============================================
  // COMPLEMENTAÇÃO / RETORNO - SAÍDA (6000)
  // ============================================
  {
    codigo: '6101',
    descricao: 'Venda de produção do estabelecimento para outro estado',
    categoria: 'SAÍDA - Venda para Outro Estado',
    tipo: 'saida'
  },

  // ============================================
  // OPERAÇÕES COM SERVIÇOS - SAÍDA (5300)
  // ============================================
  {
    codigo: '5300',
    descricao: 'Prestação de serviço de transporte de carga',
    categoria: 'SAÍDA - Serviços',
    tipo: 'saida'
  },
  {
    codigo: '5301',
    descricao: 'Prestação de serviço de transporte de passageiros',
    categoria: 'SAÍDA - Serviços',
    tipo: 'saida'
  },
  {
    codigo: '5302',
    descricao: 'Prestação de serviço (não especificado)',
    categoria: 'SAÍDA - Serviços',
    tipo: 'saida'
  },
  {
    codigo: '5303',
    descricao: 'Devolução/Devolução de valor relativo à prestação de serviço',
    categoria: 'SAÍDA - Serviços',
    tipo: 'saida'
  },

  // ============================================
  // OPERAÇÕES COM MERCADORIAS - ENTRADA (1000)
  // ============================================
  {
    codigo: '1102',
    descricao: 'Compra para revenda de mercadoria adquirida ou recebida de terceiros',
    categoria: 'ENTRADA - Compra para Revenda',
    tipo: 'entrada'
  },
  {
    codigo: '1103',
    descricao: 'Devolução de venda de mercadoria adquirida ou recebida de terceiros',
    categoria: 'ENTRADA - Devolução de Venda',
    tipo: 'entrada'
  },
  {
    codigo: '1104',
    descricao: 'Compra de amostra de mercadoria',
    categoria: 'ENTRADA - Amostra',
    tipo: 'entrada'
  },
  {
    codigo: '1120',
    descricao: 'Compra de mercadoria adquirida ou recebida de terceiros (Uso ou Consumo)',
    categoria: 'ENTRADA - Compra para Uso',
    tipo: 'entrada'
  },
  {
    codigo: '1210',
    descricao: 'Devolução de compra de mercadoria adquirida ou recebida de terceiros',
    categoria: 'ENTRADA - Devolução de Compra',
    tipo: 'entrada'
  },

  // ============================================
  // OPERAÇÕES COM SERVIÇOS - ENTRADA (1300)
  // ============================================
  {
    codigo: '1300',
    descricao: 'Aquisição de serviço de transporte de carga',
    categoria: 'ENTRADA - Serviços',
    tipo: 'entrada'
  },
  {
    codigo: '1301',
    descricao: 'Aquisição de serviço de transporte de passageiros',
    categoria: 'ENTRADA - Serviços',
    tipo: 'entrada'
  },
  {
    codigo: '1302',
    descricao: 'Aquisição de serviço (não especificado)',
    categoria: 'ENTRADA - Serviços',
    tipo: 'entrada'
  },
];

/**
 * Mapear CFOP por categoria para facilitar seleção
 */
export const CFOPsByCategoria = CFOPs.reduce((acc, cfop) => {
  if (!acc[cfop.categoria]) {
    acc[cfop.categoria] = [];
  }
  acc[cfop.categoria].push(cfop);
  return acc;
}, {} as Record<string, CFOP[]>);

/**
 * Validar se um CFOP é válido
 */
export const isValidarCFOP = (cfop: string): boolean => {
  return CFOPs.some(c => c.codigo === cfop);
};

/**
 * Obter descrição de um CFOP
 */
export const getDescricaoCFOP = (cfop: string): string => {
  const item = CFOPs.find(c => c.codigo === cfop);
  return item ? item.descricao : 'CFOP inválido';
};

/**
 * Obter CFOPs para saída (mais comuns para Ateliê)
 */
export const CFOPsSaida = CFOPs.filter(c => c.tipo === 'saida');

/**
 * CFOPs mais comuns para Ateliê Pro
 */
export const CFOPsMaisComuns = [
  CFOPs.find(c => c.codigo === '5102')!,  // Venda consumidor final
  CFOPs.find(c => c.codigo === '5120')!,  // Venda B2B
  CFOPs.find(c => c.codigo === '5103')!,  // Devolução
  CFOPs.find(c => c.codigo === '6101')!,  // Retorno de venda
  CFOPs.find(c => c.codigo === '5302')!,  // Serviço
];
